import { FC, ReactNode } from 'react';
import { LoadingSpinner } from 'shared/components/LoadingSpinner.tsx';
import { mergeClasses } from 'shared/utils/mergeClasses.ts';

type Size = 'large' | 'medium' | 'small';
type Variant = 'primary' | 'secondary' | 'outline' | 'success' | 'danger' | 'text';
type Shape = 'rounded' | 'circle';

const getVariantClassNames = (variant: Variant) => {
  switch (variant) {
    case 'primary':
      return 'bg-primary-500 hover:enabled:bg-primary-600 active:enabled:bg-primary-700 text-white [&>*]:text-white [&>.spinner]:!border-r-white [&>.spinner]:!border-t-white';
    case 'secondary':
      return 'bg-secondary-500 hover:enabled:bg-secondary-600 active:enabled:bg-secondary-700';
    case 'outline':
      return 'border border-black hover:enabled:bg-black/5 active:enabled:bg-black/10';
    case 'success':
      return 'border border-green-700 text-green-700 hover:enabled:bg-green-700/5 active:enabled:bg-green-700/10 [&>.spinner]:!border-r-green-700 [&>.spinner]:!border-t-green-700';
    case 'danger':
      return 'border border-red text-red hover:enabled:bg-red/5 active:enabled:bg-red/10';
    case 'text':
      return 'text-primary-500 hover:enabled:text-primary-600 active:enabled:text-primary-700 text-left';
    default:
      return '';
  }
};

const getSizeClassNames = (size: Size, shape: Shape) => {
  switch (size) {
    case 'small':
      if (shape === 'circle') {
        return 'p-1.5';
      }
      return 'py-1.5 px-4';
    case 'medium':
      if (shape === 'circle') {
        return 'p-2';
      }
      return 'py-2 px-6';
    case 'large':
      if (shape === 'circle') {
        return 'p-3.5';
      }
      return 'py-3.5 px-4 lg:px-6';
    default:
      return '';
  }
};

interface ButtonProps {
  className?: string;
  size?: Size;
  variant?: Variant;
  type?: HTMLButtonElement['type'];
  shape?: Shape;
  onClick?: () => void;
  id?: string;
  loading?: boolean;
  disabled?: boolean;
  children: ReactNode;
}

export const Button: FC<ButtonProps> = ({
  className = '',
  size = 'medium',
  variant = 'primary',
  type = 'button',
  shape = 'rounded',
  onClick,
  id,
  loading = false,
  disabled = false,
  children,
}) => {
  const variantClassName = getVariantClassNames(variant);
  const sizeClassName = getSizeClassNames(size, shape);

  return (
    <button
      disabled={disabled}
      className={mergeClasses(
        className,
        variantClassName,
        variant === 'text' ? 'text-xs' : sizeClassName,
        shape === 'circle' ? 'rounded-full' : 'rounded-lg',
        'flex items-center justify-center text-sm font-medium transition-all disabled:opacity-50',
      )}
      type={type}
      id={id}
      onClick={onClick}
    >
      {loading ? <LoadingSpinner size={size === 'small' ? 14 : 20} /> : children}
    </button>
  );
};
